import { TMoveToCategoryItem } from '../../MoveToCategoryPrompt';
import { getDescendantIds } from '../getDescendantIds';

/**
 * Filters orderedCategories to only include valid move destinations based on hierarchy rules.
 * Replaces "Uncategorised" with "No Parent" option.
 *
 * @param category - The category being moved
 * @param orderedCategories - Array of all categories in hierarchical order
 * @returns Filtered array of valid destination categories
 */
export const filterValidMoveToCategories = <T extends TMoveToCategoryItem>(
  category: T,
  orderedCategories: T[],
): T[] => {
  const descendantIds = getDescendantIds(category.category_id, orderedCategories);

  return orderedCategories.reduce((acc: T[], targetCategory: T) => {
    const shouldExclude =
      // Exclude the category itself
      targetCategory.category_id === category.category_id ||
      // Exclude if destination is a descendant of the category
      (targetCategory.category_id !== null && descendantIds.has(targetCategory.category_id)) ||
      // Exclude current direct parent
      (category.parent_category_id && targetCategory.category_id === category.parent_category_id) ||
      // Exclude "No Parent" for root categories
      (!category.parent_category_id && !targetCategory.category_id && !targetCategory.parent_category_id);

    if (shouldExclude) {
      return acc;
    }

    // Replace "Uncategorised" with "No Parent"
    if (targetCategory.category_id === null && targetCategory.name === 'Uncategorised') {
      return [
        ...acc,
        {
          ...targetCategory,
          name: 'No Parent (becomes new parent category)',
          category_id: null,
          parent_category_id: null,
          display_order: 0,
        } as T,
      ];
    }

    return [...acc, targetCategory];
  }, []);
};
